// ---------------------------------------------------------------------------
// User-facing formatting. Copy here follows https://tropes.fyi/tropes-md.
// ---------------------------------------------------------------------------

import {
  ROUND_INTRO_MS,
  revealHoldMs,
  revealLeadMs,
  revealPayoffMs,
} from '@countdown/shared';
import type { GameSettings, RoundType } from '@countdown/shared';
import type { LlmModelOption } from './api.js';

const ROUND_LABELS: Readonly<Record<RoundType, string>> = {
  letters: 'Letters',
  numbers: 'Numbers',
  conundrum: 'Conundrum',
};

export function roundLabel(type: RoundType): string {
  return ROUND_LABELS[type];
}

export interface RoundLabel {
  /** `L3`, `N`, `C`. For a table header, where fifteen columns have to fit. */
  short: string;
  /** `Letters 3`. For prose, and for the header's accessible name. */
  long: string;
}

/**
 * One label per round, numbered only where a type repeats.
 *
 * Both forms come from the same pass because they have to agree: the breakdown
 * column says `L3` and the best-word footnote says `Letters 3` about the same
 * round. The absolute position is deliberately not either of them — a
 * conundrum in slot 15 is "Conundrum", and saying "round 15" beside a column
 * headed Conundrum was two names for one round.
 *
 * The short letter is the first letter of the long name rather than its own
 * table, so a renamed round type cannot leave the two disagreeing.
 */
export function roundLabels(types: RoundType[]): RoundLabel[] {
  const totals = new Map<RoundType, number>();
  for (const type of types) totals.set(type, (totals.get(type) ?? 0) + 1);

  const seen = new Map<RoundType, number>();
  return types.map((type) => {
    const position = (seen.get(type) ?? 0) + 1;
    seen.set(type, position);
    const name = ROUND_LABELS[type];
    const numbered = (totals.get(type) ?? 0) > 1;
    return {
      short: numbered ? `${name[0]}${position}` : name[0],
      long: numbered ? `${name} ${position}` : name,
    };
  });
}

/**
 * Counts per round type, in the order each type first appears.
 *
 * A 15-round mix cannot be shown as 15 chips, so lists summarise: `L 10 N 4
 * C 1`. Only the lobby's builder shows one chip per round.
 */
export function groupedSequence(sequence: RoundType[]): { type: RoundType; count: number }[] {
  const grouped: { type: RoundType; count: number }[] = [];
  for (const type of sequence) {
    const existing = grouped.find((entry) => entry.type === type);
    if (existing) existing.count += 1;
    else grouped.push({ type, count: 1 });
  }
  return grouped;
}

/**
 * How long the configured game will take, near enough to plan around.
 *
 * It is an estimate and cannot be anything else: the reveal is paced per answer
 * and the lobby shows this before the room is full, so it assumes a nominal
 * table rather than the current one. Selection phases usually finish early too,
 * which the estimate does not try to model.
 */
const ASSUMED_ANSWERS = 5;

/**
 * What a round costs after the clock stops.
 *
 * The engine paces this: a lead, then one hold per answer, and the hold on the
 * last one is the trailing step the reveal runs past the end of its queue for.
 * Assuming every nominal player answered is the long end of the range, which is
 * the honest end to estimate from — a room that finishes early is never annoyed.
 * Then the client's closing slides, and the moment the host takes to press.
 *
 * **The scoreboard used to be counted here and there is no scoreboard.** The
 * reveal's closing slide is the standing, so what follows a reveal is the next
 * round.
 */
function revealMs(settings: GameSettings): number {
  // Summed from the engine's own curve rather than multiplied out of a base.
  // An entry's hold is no longer one number times a count: it is weighted by
  // where the entry sits in the queue, so the last answer gets the most and a
  // crowded round compresses toward the floor. Multiplying would overstate a
  // big room by a factor of three, and this figure is what the lobby promises
  // before anybody has joined.
  let total = revealLeadMs(settings.revealPacePercent);
  for (let index = 0; index < ASSUMED_ANSWERS; index += 1) {
    total += revealHoldMs({
      index,
      count: ASSUMED_ANSWERS,
      hasAnswer: true,
      pacePercent: settings.revealPacePercent,
    });
  }
  const payoff = revealPayoffMs(settings.revealPacePercent);
  return total + payoff.leadMs + payoff.beatMs;
}

/**
 * The host's own pause at the foot of the reveal. It has no fixed length —
 * `REVEAL_AUTO_ADVANCE_MS` is only the ceiling — and it is never nothing, so a
 * few seconds is the least dishonest thing to count.
 */
const ASSUMED_HOST_GAP_MS = 5_000;

export function estimateDurationMs(settings: GameSettings): number {
  let total = 0;

  for (const type of settings.roundSequence) {
    total += ROUND_INTRO_MS;
    if (type === 'letters') {
      total += (settings.lettersSelectionSeconds + settings.lettersPlaySeconds) * 1_000;
    } else if (type === 'numbers') {
      total += (settings.numbersSelectionSeconds + settings.numbersPlaySeconds) * 1_000;
    } else {
      total += settings.conundrumSeconds * 1_000;
    }
    total += revealMs(settings) + ASSUMED_HOST_GAP_MS;
  }

  return total;
}

export function formatDuration(ms: number): string {
  if (ms < 45_000) return 'under a minute';

  const minutes = Math.round(ms / 60_000);
  if (minutes <= 1) return 'about a minute';
  if (minutes < 60) return `about ${minutes} minutes`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourPart = hours === 1 ? 'an hour' : `${hours} hours`;
  if (rest === 0) return `about ${hourPart}`;
  return `about ${hourPart} and ${rest} minutes`;
}

export function shareUrl(roomCode: string): string {
  return `${window.location.origin}/room/${roomCode.toUpperCase()}`;
}

/**
 * `Ada`, `Ada and Bea`, `Ada, Bea and Cal`. Serial comma deliberately absent,
 * matching the rest of the copy.
 *
 * Here rather than in a screen because three of them wanted it — the reveal for
 * a team's members, the end screen for shared winners, and the round-by-round
 * table for a team row.
 */
export function listNames(names: string[]): string {
  if (names.length === 0) return 'nobody';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The public record of one finished game. One place so a link cannot drift. */
export function gameRecordPath(gameId: string): string {
  return `/game/${encodeURIComponent(gameId)}`;
}

/**
 * An AI opponent's profile, or null when the id cannot be addressed by one.
 *
 * The route is two segments — `/m/anthropic/claude-opus-5` — because an
 * OpenRouter id is `provider/name` and a `%2F` inside a path is mangled by
 * enough proxies that building the identity out of the path is the safer shape.
 *
 * Null rather than a best-effort URL for an id that does not split in two.
 * Nothing in the pinned catalogue looks like that, but a stored row is whatever
 * was stored, and a link that certainly 404s is worse than a name with no link
 * on it.
 */
export function modelPath(modelId: string): string | null {
  const parts = modelId.split('/');
  if (parts.length !== 2 || parts[0] === '' || parts[1] === '') return null;
  return `/m/${encodeURIComponent(parts[0])}/${encodeURIComponent(parts[1])}`;
}

/**
 * What to call a model on screen: the catalogue's label when the server sent
 * one, else the half of the id that names the model.
 *
 * A retired model has no label — it left the pinned list — and printing the
 * whole id in a heading is unreadable, so the provider is dropped and shown
 * separately.
 */
export function modelName(modelId: string, label: string | null): string {
  if (label !== null) return label;
  const parts = modelId.split('/');
  return parts.length === 2 ? parts[1] : modelId;
}

export function ordinal(n: number): string {
  const abs = Math.abs(n);
  // 11th, 12th and 13th break the last-digit rule, and so does every hundred
  // above them.
  const teens = abs % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  switch (abs % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/**
 * Roughly USD for one seat on this model for a game of `rounds` rounds, or null
 * when the server did not say.
 *
 * A deliberate second copy of the server's `costPerGameUsd`, for the same
 * reason `LlmModelOption` is a second copy of `LlmModel`: this side owns what it
 * renders from a payload, and the arithmetic is two multiplications. If it ever
 * grows past that it belongs in `@countdown/shared`, not in either.
 *
 * Here rather than beside that type in `api.ts` because `api.ts` is the module
 * that talks to the network, so every screen test mocks it whole — and a pure
 * function that can only be reached through a mock is a function nobody can
 * test. Six lobby tests said so the moment it was written in the wrong place.
 */
export function seatCostPerGame(model: LlmModelOption, rounds: number): number | null {
  if (!model.usualTokens) return null;
  const { prompt, completion } = model.usualTokens;
  return (
    rounds *
    ((prompt * model.inputPerMillion) / 1_000_000 +
      (completion * model.outputPerMillion) / 1_000_000)
  );
}

/**
 * A seat price, short enough to sit at the end of a row in the model picker.
 *
 * Cents, because dollars would be "$0.15" and four of the ten models round to
 * "$0.00" — a price of zero beside a model that is not free is worse than no
 * price at all. Under a penny says so rather than showing "0¢", for the same
 * reason. Above a dollar is not reachable by anything in the catalogue now and
 * is handled anyway, because the catalogue is the thing most likely to change.
 *
 * **Bare, with no unit and no "about".** Both belong to the column rather than
 * to each row: the picker's header says `model … a game` once, and ten rows
 * each repeating "a game" is ten times the ink for one fact.
 *
 * Not a width argument, though it was written as one first and measured
 * afterwards: the long form fits beside the longest name in the catalogue even
 * at 320px, with the name still untruncated. It is about repetition.
 * `seatCostLabel` is the readable version, for the accessible name — the one
 * place with no column to carry the unit.
 */
export function formatSeatCost(usd: number): string {
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  const cents = usd * 100;
  if (cents < 1) return '<1¢';
  return `${cents < 10 ? cents.toFixed(1) : Math.round(cents)}¢`;
}

/**
 * How much longer than its own average a model is given before the lobby calls
 * a clock long enough for it.
 *
 * `usualLatencyMs` is a bench AVERAGE over six boards and its own doc comment
 * says so. So a room whose clock exactly matches the average is a room where
 * roughly half this model's answers arrive too late, and quoting the average as
 * the minimum would be presenting a middle as a guarantee.
 *
 * Half as long again is a judgement, not a measurement, and it is the honest
 * kind of guess: the spread across those six boards was never recorded, so
 * there is no percentile to compute and nothing here may claim one. What the
 * ledger now writes into `llm_usage.latency_ms` is the data that will replace
 * this — when there is enough of it, take a real high percentile per model and
 * delete the multiplier rather than tuning it.
 *
 * Two figures were rejected. 1.0 (warn only when the average itself does not
 * fit) puts every borderline model one slow board away from playing nothing and
 * says nothing about it. 2.0 tells a 30-second room to double its letters clock
 * because DeepSeek V4 Pro averages 16 seconds, which is a scold rather than a
 * warning, and a warning nobody believes is one nobody reads.
 */
const LATENCY_HEADROOM = 1.5;

/**
 * The clock steps the lobby's timer sliders actually land on, restated from
 * `lobby/fields.tsx` — which restates them in turn from `sanitizeSettings`,
 * where they are module-private.
 *
 * A suggestion the control cannot be set to is not a suggestion. Rounding UP to
 * the step is what makes the number self-consistent: a host who takes the
 * advice lands on a value where the warning has gone, rather than one step
 * below it with the same paragraph still on screen.
 */
const CLOCK_STEP_SECONDS = 5;
const CLOCK_MAX_SECONDS = 300;

/**
 * The shortest round clock this model is likely to finish inside, in whole
 * slider steps, or null when the server sent no latency figure.
 *
 * This is both halves of the lobby's warning on purpose: it is the test for
 * whether a clock is too short AND the number suggested in place of it. Two
 * separate numbers — warn under X, suggest Y — is how a suggestion ends up not
 * clearing the warning it came with.
 */
export function suggestedClockSeconds(model: LlmModelOption): number | null {
  if (model.usualLatencyMs === undefined) return null;
  const wanted = (model.usualLatencyMs / 1_000) * LATENCY_HEADROOM;
  const stepped = Math.ceil(wanted / CLOCK_STEP_SECONDS) * CLOCK_STEP_SECONDS;
  // Never past the top of the slider. Nothing in the catalogue is within a
  // factor of four of this, but the catalogue is the part most likely to
  // change, and advice the host cannot follow is worse than none.
  return Math.min(CLOCK_MAX_SECONDS, Math.max(CLOCK_STEP_SECONDS, stepped));
}

/**
 * The measured average as a phrase, for the warning that names it.
 *
 * "about" is load-bearing and is why this is a function rather than a template
 * at the call site: the number underneath it is a mean over six boards, and
 * every place it reaches a player has to say so.
 */
export function usualLatencyLabel(model: LlmModelOption): string | null {
  if (model.usualLatencyMs === undefined) return null;
  const seconds = Math.max(1, Math.round(model.usualLatencyMs / 1_000));
  return seconds === 1 ? 'about a second' : `about ${seconds} seconds`;
}

/**
 * The same price as a phrase, for a screen reader.
 *
 * A row's accessible name is built from its contents, so without this one it
 * would read "Haiku 4.5, 1.3¢" — a number with no unit, and a tilde read out as
 * "tilde" by some readers, which is why the visible form has no tilde in it
 * either. Somebody who cannot see the column header should still be told what
 * the number counts.
 */
export function seatCostLabel(usd: number): string {
  if (usd * 100 < 1) return 'under 1¢ a game';
  return `about ${formatSeatCost(usd)} a game`;
}
